import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { getArticleById, getArticles, formatDuration } from '../services/articlesApi';

const TYPE_META = {
  ARTICLE:    { label: 'Article',     icon: '📄', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  VIDEO:      { label: 'Video',       icon: '🎬', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  WHITEPAPER: { label: 'White Paper', icon: '📋', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
};

/**
 * Detect a YouTube / Vimeo URL and return an embeddable iframe `src`.
 * Returns null if the URL isn't a recognized embeddable provider — in that
 * case we fall back to a direct file/native <video> render or an outbound link.
 */
const getEmbedUrl = (url) => {
  if (!url) return null;

  try {
    const u = new URL(url);

    // YouTube — watch?v=, youtu.be/, /embed/, /shorts/
    if (u.hostname.includes('youtube.com') || u.hostname === 'youtu.be') {
      let videoId = null;
      if (u.hostname === 'youtu.be') {
        videoId = u.pathname.slice(1);
      } else if (u.pathname.startsWith('/embed/')) {
        return url;
      } else if (u.pathname.startsWith('/shorts/')) {
        videoId = u.pathname.split('/')[2];
      } else {
        videoId = u.searchParams.get('v');
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    // Vimeo — vimeo.com/12345678
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * A direct, playable video file — uploaded via the doctor's "Upload file"
 * option and resolved by the backend to a presigned/S3 URL (not YouTube/Vimeo).
 */
const isDirectVideoFile = (url) =>
  !!url && /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);

const VideoPlayer = ({ videoUrl, thumbnailUrl, title }) => {
  const embedUrl = getEmbedUrl(videoUrl);

  if (embedUrl) {
    return (
      <div className="article-video-frame">
        <iframe
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          frameBorder="0"
        />
      </div>
    );
  }

  if (isDirectVideoFile(videoUrl)) {
    return (
      <div className="article-video-frame">
        <video
          src={videoUrl}
          poster={thumbnailUrl || undefined}
          controls
          preload="metadata"
        />
      </div>
    );
  }

  // Unrecognized video URL format — fall back to a thumbnail + outbound link
  return (
    <div className="article-video-frame article-video-frame--fallback">
      {thumbnailUrl && <img src={thumbnailUrl} alt="" />}
      <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="btn-primary article-video-fallback-btn">
        ▶ Watch video
      </a>
    </div>
  );
};

/** Render plain-text body as paragraphs (splits on blank lines). */
const BodyText = ({ body }) => {
  const paragraphs = body.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return (
    <div className="article-body-text">
      {paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
  );
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const navigate      = useNavigate();
  const { tenant }    = useTenant();

  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      getArticleById(articleId, tenant?.id),
      getArticles(tenant?.id),
    ])
      .then(([found, all]) => {
        if (cancelled) return;
        if (!found) { setError('Article not found.'); setLoading(false); return; }

        setArticle(found);

        // Related: same doctor or overlapping tags, excluding self
        const rel = all
          .filter(a => a.id !== articleId && (
            a.doctorId === found.doctorId ||
            (a.tags || []).some(t => (found.tags || []).includes(t))
          ))
          .slice(0, 3);
        setRelated(rel);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError('Could not load article.'); setLoading(false); } });

    return () => { cancelled = true; };
  }, [articleId, tenant?.id]);

  if (loading) return (
    <div className="page">
      <main className="page-main">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="skeleton" style={{ width: '35%', height: 16, borderRadius: 8 }} />
          <div className="skeleton" style={{ width: '100%', height: 280, borderRadius: 18 }} />
          <div className="skeleton" style={{ width: '70%', height: 28, borderRadius: 8 }} />
          <div className="skeleton" style={{ width: '100%', height: 14, borderRadius: 6 }} />
          <div className="skeleton" style={{ width: '90%', height: 14, borderRadius: 6 }} />
          <div className="skeleton" style={{ width: '80%', height: 14, borderRadius: 6 }} />
        </div>
      </main>
    </div>
  );

  if (error || !article) return (
    <div className="page">
      <main className="page-main">
        <div className="error-state"><span>⚠️</span> {error || 'Article not found.'}</div>
        <button className="btn-primary" style={{ maxWidth: 200, marginTop: 16 }} onClick={() => navigate('/')}>
          Go home
        </button>
      </main>
    </div>
  );

  const meta     = TYPE_META[article.type] || TYPE_META.ARTICLE;
  const dateStr  = article.publishedDate
    ? new Date(article.publishedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const viewsStr = article.viewCount >= 1000
    ? `${(article.viewCount / 1000).toFixed(1)}k views`
    : `${article.viewCount ?? 0} views`;

  const hasBody     = !!article.body && article.body.trim().length > 0;
  const isVideo     = article.type === 'VIDEO';
  const hasVideo    = isVideo && !!article.videoUrl;
  // External link is its own thing — a reference/source link, shown
  // regardless of content type, never conflated with the video player.
  const hasExternal = !!article.externalUrl;

  return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: 720 }}>

        {/* Breadcrumb */}
        <div className="breadcrumb" style={{ marginBottom: 20 }}>
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/')}>Health Insights</button>
          <span>›</span>
          <span className="bc-current" style={{
            maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle'
          }}>{article.title}</span>
        </div>

        {/* Hero — video player for VIDEO type, static thumbnail otherwise */}
        {hasVideo ? (
          <div style={{ marginBottom: 24 }}>
            <VideoPlayer videoUrl={article.videoUrl} thumbnailUrl={article.thumbnailUrl} title={article.title} />
          </div>
        ) : article.thumbnailUrl && (
          <div style={{
            width: '100%', height: 280, borderRadius: 18, overflow: 'hidden',
            marginBottom: 24, position: 'relative', background: '#f3f4f6',
          }}>
            <img
              src={article.thumbnailUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        )}

        {/* Type badge + time meta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 9999,
            background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {meta.icon} {meta.label}
          </span>
          {isVideo && article.videoDurationSecs && (
            <span className="t-meta">⏱ {formatDuration(article.videoDurationSecs)}</span>
          )}
          {!isVideo && article.readTimeMinutes && (
            <span className="t-meta">⏱ {article.readTimeMinutes} min read</span>
          )}
          {dateStr && <span className="t-meta">📅 {dateStr}</span>}
          <span className="t-meta">👁 {viewsStr}</span>
        </div>

        {/* Title */}
        <h1 className="t-h1" style={{ marginBottom: 16, fontSize: 'clamp(20px, 3vw, 28px)', lineHeight: 1.25 }}>
          {article.title}
        </h1>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {article.tags.map(tag => (
              <span key={tag} className="tag-chip">{tag}</span>
            ))}
          </div>
        )}

        {/* Doctor byline */}
        <div
          className="article-doctor-byline"
          style={{ cursor: article.doctorId ? 'pointer' : 'default' }}
          onClick={() => article.doctorId && navigate(`/services/doctors/${article.doctorId}/profile`)}
        >
          <img
            src={article.doctorPhoto || `https://api.dicebear.com/7.x/personas/svg?seed=${article.doctorId}`}
            alt=""
            style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e7ff', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: 14, color: '#1e1b4b', whiteSpace: 'nowrap' }}>
              {article.doctorName}
            </div>
            <div className="t-meta">{article.department}</div>
          </div>
        </div>

        {/* Summary */}
        <div className="card" style={{ marginBottom: hasBody ? 16 : 24 }}>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: '#374151',
            lineHeight: 1.8, margin: 0, fontWeight: 600,
          }}>
            {article.summary}
          </p>
        </div>

        {/* Full body — the actual article/whitepaper content */}
        {hasBody && (
          <div className="card" style={{ marginBottom: 24 }}>
            <BodyText body={article.body} />
          </div>
        )}

        {/* Video without a usable embed/file — explicit watch CTA */}
        {isVideo && !article.videoUrl && (
          <div className="card" style={{ marginBottom: 24 }}>
            <p className="t-body-sm" style={{ margin: 0 }}>This video isn't available right now.</p>
          </div>
        )}

        {/* External reference link — separate from the video player above */}
        {hasExternal && (
          <a
            href={article.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              marginBottom: 24, textDecoration: 'none', maxWidth: 280,
            }}
          >
            ↗ {isVideo ? 'View source' : 'Read full source / reference'}
          </a>
        )}

        {/* Related content */}
        {related.length > 0 && (
          <div>
            <h2 className="t-h2" style={{ marginBottom: 16 }}>Related Content</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {related.map(item => {
                const rm = TYPE_META[item.type] || TYPE_META.ARTICLE;
                return (
                  <div
                    key={item.id}
                    className="pub-card"
                    onClick={() => navigate(`/articles/${item.id}`)}
                  >
                    <div className="pub-card__thumb">
                      {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" />}
                      {item.type === 'VIDEO' && (
                        <div className="pub-card__thumb-overlay">
                          <div style={{
                            width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.92)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 2,
                          }}>
                            <svg width="9" height="10" viewBox="0 0 9 10" fill="#1e1b4b"><path d="M1 1l7 4-7 4V1z"/></svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="pub-card__body">
                      <div className="pub-card__meta">
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 9999,
                          background: rm.bg, color: rm.color, border: `1px solid ${rm.border}`,
                          fontFamily: 'DM Sans, sans-serif',
                        }}>
                          {rm.icon} {rm.label}
                        </span>
                        {item.type === 'VIDEO' && item.videoDurationSecs && (
                          <span className="t-meta">⏱ {formatDuration(item.videoDurationSecs)}</span>
                        )}
                        {item.type !== 'VIDEO' && item.readTimeMinutes && (
                          <span className="t-meta">⏱ {item.readTimeMinutes} min read</span>
                        )}
                      </div>
                      <div className="pub-card__title">{item.title}</div>
                      <p className="pub-card__summary">{item.summary}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default ArticlePage;